<template>
    <v-dialog :value="dialog" v-stack="{ open: dialog, close: hide }" :fullscreen="isMobile"
        :max-width="isMobile ? undefined : 520"
        :transition="isMobile ? 'dialog-bottom-transition' : 'dialog-transition'" @input="onInput"
        content-class="chat-dialog-content">
        <div class="modal-chat" :class="{ 'modal-chat--mobile': isMobile }">
            <div class="mc-header">
                <input type="file" ref="fileUpload" class="d-none" @change="onFileSelected" />
                <template v-if="!confirmingDelete">
                    <div class="mc-avatar">
                        <v-img v-if="clientImg" eager cover :src="clientImg" class="mc-avatar-img" />
                        <span v-else>{{ clientInitials }}</span>
                    </div>
                    <div class="mc-info">
                        <div class="mc-name">{{ clientName }}</div>
                        <div v-if="titleChat" class="mc-sub">{{ titleChat }}</div>
                    </div>
                    <div class="mc-header-actions">
                        <button-tooltip v-if="canConfigChatMode" bottom :tooltip="$t('chatMode.configure_tooltip')">
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="mc-chatmode-btn"
                                    :class="{ 'mc-chatmode-btn--locked': chatModeLocked }" v-on="on" v-bind="attrs"
                                    @click="showChatMode">
                                    <v-icon size="15">
                                        {{ chatModeLocked ? 'mdi-lock-outline' : 'mdi-message-processing-outline' }}
                                    </v-icon>
                                    <span class="mc-chatmode-label">{{ $t('chatMode.title') }}</span>
                                </button>
                            </template>
                        </button-tooltip>
                        <button-tooltip v-if="canSeeCoachProfile" bottom :tooltip="$t('common.see_profile')">
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="mc-icon-btn" v-on="on" v-bind="attrs"
                                    @click="goToCoachProfile">
                                    <v-icon size="18">mdi-account-outline</v-icon>
                                </button>
                            </template>
                        </button-tooltip>
                        <v-menu v-if="canDelete" offset-y left nudge-bottom="6" content-class="mc-menu">
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="mc-icon-btn" :aria-label="$t('common.options')"
                                    v-bind="attrs" v-on="on">
                                    <v-icon size="18">mdi-dots-vertical</v-icon>
                                </button>
                            </template>
                            <v-list dense class="py-1">
                                <v-list-item @click="confirmingDelete = true">
                                    <v-icon size="16" color="error" class="mr-2">mdi-trash-can-outline</v-icon>
                                    <v-list-item-title class="error--text">
                                        {{ $t('common.delete_conversation') }}
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                        <button-tooltip bottom :tooltip="$t('common.close')">
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="mc-icon-btn close" v-on="on" v-bind="attrs"
                                    @click="hide">
                                    <v-icon size="16">mdi-close</v-icon>
                                </button>
                            </template>
                        </button-tooltip>
                    </div>
                </template>
                <div v-else class="mc-confirm">
                    <div class="mc-confirm-text">
                        <strong>{{ $t('common.delete_conversation') }}</strong>
                        {{ $t('common.cannot_undo') }}
                    </div>
                    <button type="button" class="mc-confirm-btn" @click="confirmingDelete = false">
                        {{ $t('common.cancel') }}
                    </button>
                    <button type="button" class="mc-confirm-btn danger" :disabled="loadingDelete"
                        @click="deleteConversation">
                        {{ $t('common.delete') }}
                    </button>
                </div>
            </div>

            <div v-if="showWellness" class="mc-wellness">
                <span class="mc-ws-label">{{ wellnessLabel }}</span>
                <wellness-dots :wellness="wellnessData" />
            </div>

            <div ref="chat" class="mc-messages" @scroll="onMessagesScroll">
                <skeleton-loader v-if="loadingMessages" type="chat" />

                <template v-else>
                    <div v-if="loadingMore" class="mc-loading-more">
                        <v-progress-circular indeterminate size="20" width="2" color="secondary" />
                    </div>

                    <div v-if="messages.length === 0" class="mc-empty">
                        {{ $t('common.no_chat_messages') }}
                    </div>

                    <template v-for="day in messageGroups">
                        <div class="day-sep" :key="day.key">{{ day.day }}</div>
                        <div v-for="(group, gi) in day.groups" :key="`${day.key}-${gi}`" class="chat-msg-group"
                            :class="group.isMe ? 'me' : 'them'">
                            <div v-if="!isGroup" class="msg-meta">
                                <div class="msg-av" :class="{ 'me-av': group.isMe }">
                                    {{ group.isMe ? myInitials : clientInitials }}
                                </div>
                                <span class="msg-sender">{{ group.isMe ? $t('common.you') : clientName }}</span>
                                <span class="msg-ts">{{ formatMessageTime(group.time) }}</span>
                            </div>
                            <message-bubble v-for="(message, i) in group.items" :key="i"
                                :from="group.isMe ? 'me' : 'others'" :message="message" />
                        </div>
                    </template>

                    <div v-if="read && !isGroup" class="mc-read">
                        {{ $t('common.read') }}
                        <v-icon size="13">mdi-check-all</v-icon>
                    </div>
                </template>
            </div>

            <div class="mc-input">
                <div v-if="pending" class="chat-pending">
                    <button-tooltip top :tooltip="$t('common.delete')">
                        <template v-slot:activator="{ on, attrs }">
                            <button type="button" class="chat-icon-btn" v-on="on" v-bind="attrs" :disabled="sending"
                                @click="discardPending">
                                <v-icon size="16">$delete</v-icon>
                            </button>
                        </template>
                    </button-tooltip>
                    <div class="chat-pending-body">
                        <audio-player v-if="pendingAudio && pendingAudio.url" :src="pendingAudio.url" />
                        <div v-else class="chat-pending-file">
                            <v-icon size="16" class="mr-2">mdi-paperclip</v-icon>
                            <span class="text-truncate">{{ pendingFile.name }}</span>
                        </div>
                    </div>
                    <button-tooltip top :tooltip="$t('common.send')">
                        <template v-slot:activator="{ on, attrs }">
                            <button type="button" class="chat-send-btn" v-on="on" v-bind="attrs" :disabled="sending"
                                @click="confirmSend">
                                <v-progress-circular v-if="sending" indeterminate size="14" width="2" color="#0C0E12" />
                                <v-icon v-else size="15" color="#0C0E12">mdi-send</v-icon>
                            </button>
                        </template>
                    </button-tooltip>
                </div>
                <audio-recorder v-else ref="recorder" :cancelling="cancellingHold" @active="isRecording = $event"
                    @record="onRecorded" />
                <div v-show="!pending && !isRecording" class="chat-input-box">
                    <div class="chat-input-toolbar">
                        <button-tooltip top :tooltip="$t('common.attach')">
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="chat-icon-btn" v-on="on" v-bind="attrs"
                                    @click="$refs.fileUpload.click()">
                                    <v-icon size="16">mdi-paperclip</v-icon>
                                </button>
                            </template>
                        </button-tooltip>
                        <button-tooltip top :tooltip="$t('common.record_audio_hint')">
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="chat-icon-btn" v-on="on" v-bind="attrs"
                                    @mousedown="onMicPress" @touchstart.prevent="onMicPress">
                                    <v-icon size="16">mdi-microphone</v-icon>
                                </button>
                            </template>
                        </button-tooltip>
                    </div>
                    <textarea ref="input" v-model="message" rows="1" :placeholder="$t('common.send_placeholder')"
                        @input="growInput" @keydown.enter.exact.prevent="send" />
                    <button-tooltip top :tooltip="$t('common.send')">
                        <template v-slot:activator="{ on, attrs }">
                            <button type="button" class="chat-send-btn" v-on="on" v-bind="attrs" :disabled="sending"
                                @click="send">
                                <v-progress-circular v-if="sending" indeterminate size="14" width="2" color="#0C0E12" />
                                <v-icon v-else size="15" color="#0C0E12">mdi-send</v-icon>
                            </button>
                        </template>
                    </button-tooltip>
                </div>
            </div>

            <chat-mode-dialog v-if="canConfigChatMode" ref="chatMode" />
        </div>
    </v-dialog>
</template>

<script>

import { db, Timestamp, storageRef } from '@/utils/Firebase'
import { getInitials } from '@/utils/Common'
import MessageBubble from "@/components/common/chat/MessageBubble";
import WellnessDots from "@/components/student/WellnessDots";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import AudioPlayer from "@/components/common/AudioPlayer";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import AudioRecorder from "@/components/common/AudioRecorder";
import { mediaExtension, DEFAULT_AUDIO_MIME_TYPE } from '@/utils/Recorder'
import { findChat } from '@/utils/Messages'
import ChatModeDialog from "@/components/coach/ChatModeDialog";

const MESSAGES_INITIAL = 80
const MESSAGES_PAGE = 50
const HOLD_THRESHOLD = 500

export default {
    components: { MessageBubble, WellnessDots, SkeletonLoader, AudioPlayer, ButtonTooltip, AudioRecorder, ChatModeDialog },
    props: {
        clientId: {
            type: String,
            default: null,
        },
        collectionName: {
            type: String,
            default: 'privateChats'
        },
        isCompanyChat: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            dialog: false,
            message: '',
            messages: [],
            client: null,
            chatId: null,
            loadingMessages: true,
            windowHeight: window.innerHeight,
            firstMessage: false,
            sending: false,
            isRecording: false,
            pendingFile: null,
            pendingAudio: null,
            cancellingHold: false,
            holdStart: null,
            confirmingDelete: false,
            loadingDelete: false,
            messagesLimit: MESSAGES_INITIAL,
            loadingMore: false,
            hasMore: true,
        }
    },
    computed: {
        clientName(vm = this) {
            return vm.client?.name || ''
        },
        clientInitials(vm = this) {
            return getInitials(vm.clientName)
        },
        messageGroups(vm = this) {
            const days = []
            let currentKey = null
            for (const message of vm.messages) {
                const date = vm.toDate(message.timestamp)
                const key = date ? vm.$moment(date).format('YYYY-MM-DD') : 'unknown'
                if (key !== currentKey) {
                    currentKey = key
                    days.push({ key, day: date ? vm.$moment(date).format('D MMM YYYY') : '', groups: [] })
                }
                const day = days[days.length - 1]
                const isMe = !vm.receptorMessage(message.from)
                const last = day.groups[day.groups.length - 1]
                if (last && last.isMe === isMe && last.from === message.from) {
                    last.items.push(message)
                    last.time = message.timestamp
                } else {
                    day.groups.push({ from: message.from, isMe, items: [message], time: message.timestamp })
                }
            }
            return days
        },
        myInitials(vm = this) {
            return getInitials(vm.account?.name || '')
        },
        read(vm = this) {
            if (!vm.dialog || !vm.chatId || vm.messages.length === 0) return false
            const lastMessage = vm.messages[vm.messages.length - 1]
            if (lastMessage.from === vm.currentUser.uid) return lastMessage.read
            return false
        },
        clientImg(vm = this) {
            return vm.client?.avatar || null
        },
        titleChat(vm = this) {
            if (vm.isCompanyChat) return vm.$t('common.chat')
            if (vm.isGroup) return vm.$t('common.group_chat')
            if (vm.account?.accountType === 'student') return vm.$t('coach.your_coach')
            if (vm.$route.name === 'coach.students.chats') return vm.$t('common.chat')
            if (vm.account?.accountType === 'coach') return vm.$t('student.chat')
            return ''
        },
        isGroup(vm = this) {
            return vm.collectionName !== 'privateChats'
        },
        isCoachChat(vm = this) {
            return !vm.isGroup && !vm.isCompanyChat
        },
        showWellness(vm = this) {
            if (!vm.isCoachChat) return false
            if (vm.accountType === 'coach') return !!vm.client
            return vm.accountType === 'student'
        },
        wellnessData(vm = this) {
            if (vm.accountType === 'student') return vm.account?.lastWellness || []
            return vm.client?.lastWellness || []
        },
        wellnessLabel(vm = this) {
            return vm.accountType === 'student' ? vm.$t('common.my_wellness') : vm.$t('common.wellness')
        },
        canSeeCoachProfile(vm = this) {
            return vm.accountType === 'student' && vm.isCoachChat && !!vm.clientId
        },
        canConfigChatMode(vm = this) {
            return vm.accountType === 'coach' && vm.isCoachChat && !!vm.clientId
        },
        chatModeLocked(vm = this) {
            return !vm.can('chatMode')
        },
        canDelete(vm = this) {
            if (!vm.chatId) return false
            return vm.accountType === 'coach' || vm.accountType === 'company'
        },
        pending(vm = this) {
            return !!(vm.pendingAudio || vm.pendingFile)
        },
        chatType(vm = this) {
            return vm.isCompanyChat ? 'company_coach' : 'coach_student'
        }
    },
    mounted() {
        const vm = this
        vm._resetView = () => {
            vm.windowHeight = window.innerHeight
            vm.scrollToEnd()
        }
        window.addEventListener('resize', vm._resetView)
        vm.reloadChat()
    },
    beforeDestroy() {
        if (this._resetView) window.removeEventListener('resize', this._resetView)
        this.removeHoldListeners()
        this.cancelRecording()
    },
    methods: {
        toDate(timestamp) {
            if (!timestamp) return null
            if (typeof timestamp.toDate === 'function') return timestamp.toDate()
            if (timestamp.seconds) return new Date(timestamp.seconds * 1000)
            return new Date(timestamp)
        },
        formatMessageTime(timestamp) {
            const vm = this
            const date = vm.toDate(timestamp)
            if (!date) return ''
            const m = vm.$moment(date)
            if (vm.$moment().diff(m, 'minutes') < 60) return m.fromNow()
            return m.format('HH:mm')
        },
        growInput() {
            const vm = this
            const el = vm.$refs.input
            if (!el) return
            el.style.height = 'auto'
            el.style.height = `${Math.min(el.scrollHeight, 80)}px`
        },
        onInput(val) {
            if (!val) this.hide()
        },
        async show(chatId, client) {
            const vm = this
            if (chatId) {
                vm.chatId = chatId
                vm.client = client
                vm.getMessages()
            }
            vm.dialog = true
            await vm.reloadChat()
            vm.$nextTick(() => vm.scrollToEnd())
        },
        hide() {
            const vm = this
            const closedChatId = vm.chatId
            vm.$emit('hideChat', { chatId: closedChatId })
            if (vm.chatId) { try { vm.$unbind('messages') } catch (e) {} }
            vm.chatId = null
            vm.client = null
            vm.firstMessage = false
            vm.messages = []
            vm.message = ''
            vm.confirmingDelete = false
            vm.discardPending()
            vm.dialog = false
        },
        goToCoachProfile() {
            const vm = this
            const coachId = vm.clientId
            if (!coachId) return
            if (vm.$route.name === 'student.coach-profile' && vm.$route.params.uid === coachId) return vm.hide()
            vm.pushRoute({ name: 'student.coach-profile', params: { uid: coachId } })
        },
        showChatMode() {
            const vm = this
            if (!vm.clientId) return
            if (!vm.requirePlan('chatMode')) return
            vm.$refs.chatMode.show({ id: vm.clientId, name: vm.clientName }, vm.currentUser?.uid)
        },
        markLastMessageAsRead() {
            const vm = this
            if (!vm.dialog || !vm.chatId || vm.messages.length === 0) return
            const lastMessage = vm.messages[vm.messages.length - 1]
            if (!lastMessage?.id) return
            if (lastMessage.from === vm.currentUser.uid) return
            if (lastMessage.read) return
            db.collection(vm.collectionName).doc(vm.chatId).collection('messages').doc(lastMessage.id).update({ read: true }).catch(() => {})
            db.collection(vm.collectionName).doc(vm.chatId).update({ color: null, 'lastMessage.read': true }).catch(() => {})
        },
        async reloadChat() {
            const vm = this
            if (!!vm.clientId && !vm.chatId) await vm.chargeChatId()
        },
        async saveMessage(payload) {
            const vm = this
            const chatRef = db.collection(vm.collectionName).doc(vm.chatId)
            const messageRef = await chatRef.collection('messages').add(payload)
            try {
                await chatRef.update({ lastMessage: payload })
            } catch (err) {
                if (err?.code !== 'not-found') throw err
                try { await messageRef.delete() } catch (e) { /* noop */ }
                const missingChatId = vm.chatId
                vm.toast(vm.$t('common.chat_no_longer_available'), 'error')
                vm.$emit('deleted', { chatId: missingChatId })
                vm.hide()
                return false
            }
            return true
        },
        async send() {
            const vm = this
            if (!vm.message) return;
            vm.sending = true

            let payload = {
                message: vm.message,
                from: vm.currentUser.uid,
                read: vm.collectionName === 'groupPrivateChats',
                timestamp: Timestamp.now(),
                type: 'text'
            }

            vm.message = ''
            vm.$nextTick(() => vm.growInput())
            if (vm.firstMessage) {
                await vm.createChatId()
                vm.firstMessage = false
            }

            if (!vm.chatId || !vm.collectionName) { vm.sending = false; return }

            const saved = await vm.saveMessage(payload)
            if (!saved) { vm.sending = false; return }
            vm.scrollToEnd()

            if (vm.$refs.input) vm.$refs.input.focus()
            vm.sending = false
        },
        scrollToEnd() {
            const vm = this
            vm.$nextTick(() => {
                if (!!vm.$refs.chat && vm.$refs.chat.scrollHeight !== undefined) {
                    vm.$refs.chat.scrollTop = vm.$refs.chat.scrollHeight
                }
            })
        },
        receptorMessage(id) {
            const vm = this
            return id === vm.clientId;
        },
        async getMessages() {
            const vm = this
            vm.messagesLimit = MESSAGES_INITIAL
            vm.hasMore = true
            vm.loadingMore = false
            await vm.bindMessages()
        },
        async bindMessages() {
            const vm = this
            try {
                await vm.$bind('messages', db.collection(vm.collectionName).doc(vm.chatId).collection('messages').orderBy('timestamp').limitToLast(vm.messagesLimit))
                vm.hasMore = vm.messages.length >= vm.messagesLimit
            } catch (err) {
                console.log('Error trying to map chats', err.message)
            } finally {
                vm.loadingMessages = false
            }
        },
        async loadMoreMessages() {
            const vm = this
            if (vm.loadingMore || !vm.hasMore || !vm.chatId) return
            const el = vm.$refs.chat
            const prevHeight = el ? el.scrollHeight : 0
            vm.loadingMore = true
            vm.messagesLimit += MESSAGES_PAGE
            await vm.bindMessages()
            vm.$nextTick(() => {
                if (el) el.scrollTop = el.scrollHeight - prevHeight
                vm.loadingMore = false
            })
        },
        onMessagesScroll() {
            const vm = this
            const el = vm.$refs.chat
            if (!el || vm.loadingMessages) return
            if (el.scrollTop <= 48 && vm.hasMore && !vm.loadingMore) vm.loadMoreMessages()
        },
        onFileSelected() {
            const vm = this
            const file = vm.$refs.fileUpload.files[0]
            if (file) vm.pendingFile = file
            vm.$refs.fileUpload.value = ''
        },
        discardPending() {
            const vm = this
            vm.cancelRecording()
            if (vm.pendingAudio?.url) {
                try { URL.revokeObjectURL(vm.pendingAudio.url) } catch (e) { /* noop */ }
            }
            vm.pendingAudio = null
            vm.pendingFile = null
        },
        async confirmSend() {
            const vm = this
            if (vm.sending) return
            if (vm.pendingAudio) {
                await vm.sendAudio(vm.pendingAudio.file)
            } else if (vm.pendingFile) {
                await vm.uploadTask(vm.pendingFile)
            }
            vm.discardPending()
        },
        async deleteConversation() {
            const vm = this
            if (vm.loadingDelete) return
            if (!vm.chatId) { vm.confirmingDelete = false; return vm.hide() }
            vm.loadingDelete = true
            await db.collection(vm.collectionName).doc(vm.chatId).delete()
            const deletedChatId = vm.chatId
            vm.loadingDelete = false
            vm.confirmingDelete = false
            vm.toast(vm.$t('common.chat_deleted'))
            vm.$emit('deleted', { chatId: deletedChatId })
            vm.hide()
        },
        async uploadTask(file) {
            const vm = this
            vm.sending = true

            try {
                let uniqueName = `${Date.now()}-${file.name}`
                let savePath = `/${vm.currentUser.uid}/chats/${vm.clientId}/${uniqueName}`
                let uploadTask = await storageRef.child(savePath).put(file)
                let downloadURL = await uploadTask.ref.getDownloadURL()

                let payload = {
                    message: file.name,
                    from: vm.currentUser.uid,
                    read: vm.collectionName === 'groupPrivateChats',
                    timestamp: Timestamp.now(),
                    type: 'attachment',
                    file: { savepath: savePath, downloadURL: downloadURL }
                }

                if (vm.firstMessage) {
                    await vm.createChatId()
                    vm.firstMessage = false
                }

                if (!vm.chatId || !vm.collectionName) return

                const saved = await vm.saveMessage(payload)
                if (!saved) return

                vm.scrollToEnd()
            } catch (e) {
                console.error('[chat] attachment send failed', e)
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.sending = false
            }
        },
        async chargeChatId() {
            const vm = this
            vm.loadingMessages = true

            if (!vm.client) {
                const clientRef = await db.collection('users').doc(vm.clientId).get()
                vm.client = clientRef.data()
            }

            let coachId = vm.clientId
            let studentId = vm.currentUser.uid
            if (vm.accountType === 'coach' || vm.accountType === 'company') {
                coachId = vm.currentUser.uid
                studentId = vm.clientId
            }

            const chat = await findChat(vm.collectionName, vm.chatType, coachId, studentId)

            if (chat) {
                vm.chatId = chat.id
                vm.getMessages()
            } else {
                vm.firstMessage = true
                vm.loadingMessages = false
            }
        },
        async createChatId() {
            const vm = this
            if (!vm.client) {
                const clientRef = await db.collection('users').doc(vm.clientId).get()
                vm.client = clientRef.data()
            }

            let coachId = vm.clientId
            let studentId = vm.currentUser.uid
            if (vm.accountType === 'coach' || vm.accountType === 'company') {
                coachId = vm.currentUser.uid
                studentId = vm.clientId
            }

            const existing = await findChat(vm.collectionName, vm.chatType, coachId, studentId)
            if (existing) {
                vm.chatId = existing.id
                return vm.getMessages()
            }

            const chat = await db.collection(vm.collectionName).add({
                type: vm.chatType,
                participants: [coachId, studentId]
            })
            vm.chatId = chat.id
            vm.getMessages()
        },
        onRecorded({ file, url }) {
            this.pendingAudio = { file, url }
        },
        onMicPress(event) {
            const vm = this

            vm.cancellingHold = false
            vm.holdStart = { at: Date.now() }

            window.addEventListener('mouseup', vm.onMicRelease)
            window.addEventListener('touchend', vm.onMicRelease)
            window.addEventListener('touchcancel', vm.onMicRelease)
            window.addEventListener('mousemove', vm.onMicMove)
            window.addEventListener('touchmove', vm.onMicMove)

            vm.$refs.recorder.start()
        },
        onMicMove(event) {
            const vm = this
            if (!vm.holdStart || !vm.$refs.recorder) return

            const { x, y } = vm.pointerPosition(event)
            vm.cancellingHold = vm.$refs.recorder.isOverDeleteZone(x, y)
        },
        onMicRelease() {
            const vm = this
            if (!vm.holdStart) return

            const heldFor = Date.now() - vm.holdStart.at
            const cancelled = vm.cancellingHold

            vm.holdStart = null
            vm.cancellingHold = false
            vm.removeHoldListeners()

            if (!vm.$refs.recorder) return
            if (cancelled) return vm.$refs.recorder.cancel()
            if (heldFor < HOLD_THRESHOLD) return

            vm.$refs.recorder.stop()
        },
        removeHoldListeners() {
            const vm = this
            window.removeEventListener('mouseup', vm.onMicRelease)
            window.removeEventListener('touchend', vm.onMicRelease)
            window.removeEventListener('touchcancel', vm.onMicRelease)
            window.removeEventListener('mousemove', vm.onMicMove)
            window.removeEventListener('touchmove', vm.onMicMove)
        },
        pointerPosition(event) {
            const point = event.touches?.[0] || event.changedTouches?.[0] || event
            return { x: point.clientX || 0, y: point.clientY || 0 }
        },
        cancelRecording() {
            const vm = this
            if (vm.$refs.recorder) vm.$refs.recorder.cleanup()
            vm.isRecording = false
        },
        async uploadAudioToStorage(audioFile) {
            const vm = this;
            const contentType = audioFile.type || DEFAULT_AUDIO_MIME_TYPE
            const savePath = `/${vm.currentUser.uid}/chats/${vm.clientId}/${Date.now()}.${mediaExtension(contentType)}`
            const uploadTask = await storageRef.child(savePath).put(audioFile, { contentType });
            const downloadURL = await uploadTask.ref.getDownloadURL();
            return downloadURL;
        },
        async sendAudio(audioFile) {
            const vm = this;

            vm.sending = true;

            try {
                const url = await vm.uploadAudioToStorage(audioFile);

                const payload = {
                    message: url,
                    from: vm.currentUser.uid,
                    read: vm.collectionName === 'groupPrivateChats',
                    timestamp: Timestamp.now(),
                    type: 'audio'
                };

                if (vm.firstMessage) {
                    await vm.createChatId();
                    vm.firstMessage = false;
                }

                const saved = await vm.saveMessage(payload);
                if (!saved) return;

                vm.scrollToEnd();
            } catch (e) {
                console.error('[chat] audio send failed', e);
                vm.toast(vm.$t('common.error_generic'), 'error');
            } finally {
                vm.sending = false;
            }
        }
    },
    watch: {
        read(val) {
            if (val) this.scrollToEnd()
        },
        loadingMessages(val) {
            if (!val) this.scrollToEnd()
        },
        messages() {
            if (this.loadingMore) return
            this.scrollToEnd()
            this.markLastMessageAsRead()
        },
        clientId(val) {
            if (!!val && !this.chatId) this.chargeChatId()
        }
    },
}

</script>

<style scoped>
.modal-chat {
    width: 100%;
    height: 82vh;
    max-height: 720px;
    border-radius: 14px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.modal-chat--mobile {
    height: 100%;
    max-height: none;
    border-radius: 0;
    border: none;
}

.mc-header {
    height: calc(60px + var(--safe-area-inset-top, 0px));
    min-height: calc(60px + var(--safe-area-inset-top, 0px));
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 14px;
    padding-top: var(--safe-area-inset-top, 0px);
    border-bottom: 1px solid var(--v-outline-base);
    flex-shrink: 0;
}

.mc-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    flex-shrink: 0;
    background: var(--brand-gradient);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    color: #090B0F;
    overflow: hidden;
}

.mc-avatar-img {
    width: 100%;
    height: 100%;
}

.mc-info {
    flex: 1;
    min-width: 0;
}

.mc-name {
    font-size: 14px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.mc-sub {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.mc-header-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
}

.mc-chatmode-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 30px;
    padding: 0 12px;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(204, 255, 51, 0.12), rgba(51, 153, 153, 0.12));
    border: 1px solid rgba(204, 255, 51, 0.4);
    color: var(--v-primary-base);
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
    cursor: pointer;
    transition: opacity .15s, border-color .15s;
}

.mc-chatmode-btn:hover {
    opacity: .85;
}

.mc-chatmode-btn ::v-deep .v-icon {
    color: inherit !important;
}

.theme--light .mc-chatmode-btn {
    background: rgba(51, 153, 153, 0.1);
    border-color: rgba(51, 153, 153, 0.4);
    color: var(--v-accent-base);
}

.mc-chatmode-btn.mc-chatmode-btn--locked,
.theme--light .mc-chatmode-btn.mc-chatmode-btn--locked {
    background: var(--v-surface2-base);
    border-color: var(--v-outline-base);
    color: var(--v-txtMuted-base);
}

@media (max-width: 599px) {
    .mc-chatmode-btn {
        padding: 0 9px;
    }

    .mc-chatmode-label {
        display: none;
    }
}

.mc-icon-btn {
    width: 30px;
    height: 30px;
    border-radius: 7px;
    border: none;
    background: transparent;
    color: var(--v-txtMuted-base);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background .15s, color .15s;
    flex-shrink: 0;
}

.mc-icon-btn .v-icon {
    color: inherit;
}

.mc-icon-btn:hover {
    background: var(--v-surface2-base);
    color: var(--v-txtSecondary-base);
}

.mc-icon-btn.close:hover {
    background: rgba(204, 51, 51, .15);
    color: var(--v-error-base);
}

.mc-messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.mc-loading-more {
    display: flex;
    justify-content: center;
    padding: 4px 0 8px;
}

.mc-empty {
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
    padding: 24px 0;
}

.day-sep {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--v-txtMuted-base);
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .06em;
    flex-shrink: 0;
}

.day-sep::before,
.day-sep::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--v-outline-base);
}

.chat-msg-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.chat-msg-group.me {
    align-items: flex-end;
}

.chat-msg-group.them {
    align-items: flex-start;
}

.msg-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 4px;
}

.chat-msg-group.me .msg-meta {
    flex-direction: row-reverse;
}

.msg-av {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    font-weight: 700;
    color: #0C0E12;
    background: var(--brand-gradient);
    flex-shrink: 0;
}

.msg-av.me-av {
    background: rgba(51, 153, 153, 0.2);
    color: var(--v-accent-base);
}

.msg-sender {
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.msg-ts {
    font-size: 10px;
    color: var(--v-txtMuted-base);
}

.mc-read {
    align-self: flex-end;
    font-size: 11px;
    color: var(--v-txtMuted-base);
    display: flex;
    align-items: center;
    gap: 3px;
}

.mc-read .v-icon {
    color: var(--v-secondary-base);
}

.mc-input {
    border-top: 1px solid var(--v-outline-base);
    padding: 10px 12px;
    padding-bottom: calc(10px + var(--safe-area-inset-bottom, 0px));
    background: var(--v-surface-base);
    flex-shrink: 0;
}

.chat-input-box {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    background: var(--v-field-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    padding: 7px 8px 7px 10px;
    transition: border-color .15s;
}

.chat-input-box:focus-within {
    border-color: var(--v-accent-base);
}

.chat-input-toolbar {
    display: flex;
    align-items: center;
    gap: 2px;
}

.chat-icon-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: var(--v-txtMuted-base);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background .15s;
}

.chat-icon-btn .v-icon {
    color: inherit;
}

.chat-icon-btn:hover {
    background: var(--v-outline-base);
    color: var(--v-txtSecondary-base);
}

.chat-input-box textarea {
    flex: 1;
    background: none;
    border: none;
    outline: none;
    font-family: inherit;
    font-size: 13px;
    color: var(--v-txtPrimary-base);
    resize: none;
    min-height: 32px;
    max-height: 80px;
    line-height: 1.5;
    padding: 6px 0;
}

.chat-input-box textarea::placeholder {
    color: var(--v-txtMuted-base);
}

.chat-send-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: var(--brand-gradient);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: opacity .15s;
}

.chat-send-btn:disabled {
    opacity: .6;
    cursor: default;
}

.chat-send-btn:not(:disabled):hover {
    opacity: .85;
}

.mc-wellness {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 14px;
    border-bottom: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    flex-shrink: 0;
    overflow-x: auto;
}

.mc-ws-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .06em;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
    flex-shrink: 0;
}

.chat-pending {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--v-field-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    padding: 7px 8px;
}

.chat-pending-body {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
}

.chat-pending-file {
    display: flex;
    align-items: center;
    min-width: 0;
    font-size: 13px;
    color: var(--v-txtPrimary-base);
}

.chat-pending-file .v-icon {
    color: var(--v-txtMuted-base);
}

.mc-confirm {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    background: rgba(204, 51, 51, .07);
    border-radius: 8px;
    padding: 6px 10px;
}

.mc-confirm-text {
    flex: 1;
    font-size: 12px;
    color: var(--v-txtSecondary-base);
    line-height: 1.4;
}

.mc-confirm-text strong {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: var(--v-error-base);
    margin-bottom: 2px;
}

.mc-confirm-btn {
    height: 28px;
    border-radius: 6px;
    border: 1px solid var(--v-outline-base);
    background: transparent;
    color: var(--v-txtSecondary-base);
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    padding: 0 10px;
    cursor: pointer;
    white-space: nowrap;
    transition: background .15s, color .15s;
}

.mc-confirm-btn:hover {
    background: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
}

.mc-confirm-btn.danger {
    background: rgba(204, 51, 51, .18);
    border-color: rgba(204, 51, 51, .35);
    color: var(--v-error-base);
}

.mc-confirm-btn.danger:hover {
    background: rgba(204, 51, 51, .28);
}

.mc-confirm-btn:disabled {
    opacity: .6;
    cursor: default;
}
</style>

<style>
.chat-dialog-content {
    box-shadow: none !important;
    border-radius: 14px !important;
    overflow: visible;
}

.chat-dialog-content.v-dialog--fullscreen {
    border-radius: 0 !important;
}
</style>
